import { useRouter } from "next/router";
import NextLink from "next/link";
import { useTranslation } from "next-i18next";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import BreadcrumbsComponent from "@atoms/Breadcrumbs/Breadcrumbs";
import ButtonHelp from "@atoms/ButtonHelp";
import { Section } from "@atoms/Section";
import Button from "@ds/components/Button";
import DynamicImage from "@ds/components/DynamicImage";
import Text from "@ds/components/Text";
import color from "@definitions/mui/color";
import { isEmpty } from "@helpers/common";
import useAction from "../../hooks/useAction";
import ChildProducts from "./container/ChildProducts";
import useStyles from "./styles";
import { IMG_WHATSAPP } from "@config/images";
import YoutubePlayer from "@fragments/YoutubePlayer/youtubePlayer";
import useResponsive from "@helpers/useResponsive";
import { isAllowInterest, isAllowWhatsapp } from "@config/feature";

const ProductLevelZero: React.FC<any> = ({ data }) => {
  const {
    breadcrumbs,
    productName,
    productDocument,
    productYoutubeId,
    productImage,
    childProduct,
    uvp: uvpProduct,
    title,
    story,
  } = data;

  const router = useRouter();
  const levelZero = `${router.query?.level_zero}`;
  const { isVisible, onWAClick } = useAction(productName);
  const classes = useStyles({ isVisible });
  const { t } = useTranslation("common");
  const hasChildProduct = childProduct?.length > 0;
  const uvpData = uvpProduct[0];
  const isTablet = useResponsive("lg");
  const tabletPadding = isTablet ? "110px" : "auto";

  return (
    <>
      <Box position="relative" mt="70px" minHeight="100vh">
        <Box bgcolor={color.grey.mid} px="16px" py={1} width="100%">
          <Container maxWidth="lg" disableGutters>
            <BreadcrumbsComponent
              homeUrl={{ label: t("root"), productPath: "/" }}
              breadcrumbs={breadcrumbs}
            />
          </Container>
        </Box>
        <Section bgcolor="#fff" maxWidth="full-width" px={tabletPadding}>
          <Container maxWidth="lg" disableGutters>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <Grid item xs={7}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  mr={4}
                >
                  <Text variant="h1" weight="700">
                    {title}
                  </Text>
                  <Box mt={2}>
                    <Text color="inactive-secondary" variant="body1" weight="600">
                      {story}
                    </Text>
                  </Box>
                  <Box display="flex" gap={2} mt={4}>
                    {isAllowInterest && (
                      <NextLink href={`/${levelZero}/interest-form`}>
                        <Button size="large">{t("interest.button")}</Button>
                      </NextLink>
                    )}
                    {!isEmpty(productDocument) && (
                      <NextLink href={productDocument?.mediaPath} target="_blank">

                        <Button size="large" variant="secondary">
                          {t("interest.learn")}
                        </Button>

                      </NextLink>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={5}>
                <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
                  <Box maxWidth={427}>
                    {productYoutubeId ? (
                      <YoutubePlayer image={productImage} productYoutubeId={productYoutubeId} />
                    ) : (
                      <DynamicImage
                        src={productImage?.mediaPath}
                        alt={productImage?.mediaName}
                        borderRadius={6}
                        priority
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Section>
        {uvpData && (
          <Section bgcolor="#F5F7FA" maxWidth="full-width" px={tabletPadding} pt={4} pb={5}>
            <Container maxWidth="lg" disableGutters>
              <Grid container justifyContent="space-between" alignItems="center" mb={4} mt={3}>
                <Grid item xs={4}>
                  <Box display="flex" justifyContent="flex-start" alignItems="flex-start">
                    <Box
                      component="img"
                      src={uvpData?.uvpImage?.mediaPath}
                      alt={uvpData?.mediaName}
                      sx={{
                        borderRadius: "12px",
                        width: "100%",
                        height: "134px",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    gap={2}
                    flexWrap="nowrap"
                    ml={5}
                  >
                    <Text variant="h2" weight="700">
                      {uvpData?.uvpTitle}
                    </Text>
                    <Text color="inactive-secondary" weight="600">
                      {uvpData?.uvpDescription}
                    </Text>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Section>
        )}
        {hasChildProduct && (
          <Section
            title={t("pdp.solution")}
            description={title}
            maxWidth="full-width"
            px={tabletPadding}
            pt={5}
          >
            <Container maxWidth="lg" disableGutters>
              <ChildProducts childProduct={childProduct || []} pathParent={levelZero} />
            </Container>
          </Section>
        )}
        <Section
          id="interestSection"
          variant="gray"
          py="60px"
          maxWidth="full-width"
          px={tabletPadding}
        >
          <Container maxWidth="lg" disableGutters>
            <Box
              paddingY="60px"
              borderRadius="16px"
              sx={{
                textAlign: "center",
                // eslint-disable-next-line max-len
                background: `linear-gradient(rgba(0,0,0,0.5),rgba(0,0,0,0.8)), url("${productImage?.mediaPath}") center/cover no-repeat`,
              }}
            >
              <Box maxWidth={680} margin="auto">
                <Text className={classes.title} variant="h2" weight="700" color="white">
                  {t("pdp.interest.title")} {title}
                </Text>
                <Box mb="40px">
                  <Text variant="body1" color="white">
                    {t("pdp.interest.desc")} {title}
                  </Text>
                </Box>
              </Box>
              <Box className={classes.interestButtonContainer}>
                {isAllowWhatsapp && (
                  <Button
                    onClick={onWAClick}
                    size="large"
                    variant="outlined"
                    width="230px"
                    className={classes.whatsAppButton}
                    startIcon={<img src={IMG_WHATSAPP} alt="whatsapp" />}
                  >
                    {t("interest.whatsapp")}
                  </Button>
                )}
                {isAllowInterest && (
                  <NextLink href={`/${levelZero}/interest-form`}>

                    <Button size="large" width="230px">
                      {t("pdp.interest.button")}
                    </Button>

                  </NextLink>
                )}
              </Box>
            </Box>
          </Container>
        </Section>
        <ButtonHelp />
      </Box>
    </>
  );
};

export default ProductLevelZero;
